import { isAuthStatus } from './auth';

export const ALL_WATCHLIST_ID = '__all__';
export const INDEXES_WATCHLIST_ID = '__indexes__';
export const MAG7_WATCHLIST_ID = '__mag7__';

export const BUILT_IN_WATCHLISTS = [
  {
    id: INDEXES_WATCHLIST_ID,
    name: 'INDEXES',
    tickers: ['SPY', 'SPX', 'QQQ', 'IWM'],
  },
  {
    id: ALL_WATCHLIST_ID,
    name: 'ALL',
    tickers: null,
  },
  {
    id: MAG7_WATCHLIST_ID,
    name: 'MAG7',
    tickers: ['AAPL', 'TSLA', 'GOOGL', 'META', 'MSFT', 'NVDA', 'AMZN'],
  },
] as const;

export const DEFAULT_WATCHLIST_ID = INDEXES_WATCHLIST_ID;

const BUILT_IN_WATCHLIST_IDS = new Set<string>(
  BUILT_IN_WATCHLISTS.map((watchlist) => watchlist.id),
);

export function isBuiltInWatchlistId(watchlistId: string): boolean {
  return BUILT_IN_WATCHLIST_IDS.has(watchlistId);
}

export interface WatchlistSymbol {
  ticker: string;
  position: number;
}

export interface UserWatchlist {
  id: string;
  name: string;
  position: number;
  symbols: WatchlistSymbol[];
}

export interface WatchlistCollection {
  watchlists: UserWatchlist[];
  limits: {
    maxWatchlists: number;
    maxSymbolsPerWatchlist: number;
    maxNameLength: number;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export class WatchlistRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'WatchlistRequestError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init);
  let json: ApiResponse<T>;
  try {
    json = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new WatchlistRequestError('Failed to load watchlists', response.status);
  }
  if (!response.ok || !json.success || json.data == null) {
    throw new WatchlistRequestError(json.error ?? 'Failed to update watchlists', response.status);
  }
  return json.data;
}

function jsonRequest(method: string, body?: unknown): RequestInit {
  return {
    method,
    headers: body == null ? undefined : { 'Content-Type': 'application/json' },
    body: body == null ? undefined : JSON.stringify(body),
  };
}

export function fetchWatchlists(): Promise<WatchlistCollection> {
  return request('/api/watchlists', { cache: 'no-store' });
}

export function createWatchlist(name: string, tickers: readonly string[]): Promise<UserWatchlist> {
  return request('/api/watchlists', jsonRequest('POST', { name, tickers }));
}

export function renameWatchlist(
  watchlistId: string,
  name: string,
): Promise<{ id: string; name: string }> {
  return request(
    `/api/watchlists/${encodeURIComponent(watchlistId)}`,
    jsonRequest('PATCH', { name }),
  );
}

export function deleteWatchlist(watchlistId: string): Promise<{ id: string }> {
  return request(`/api/watchlists/${encodeURIComponent(watchlistId)}`, jsonRequest('DELETE'));
}

export function reorderWatchlists(watchlistIds: readonly string[]): Promise<string[]> {
  return request('/api/watchlists/order', jsonRequest('PUT', { watchlistIds }));
}

export function replaceWatchlistSymbols(
  watchlistId: string,
  tickers: readonly string[],
): Promise<{ id: string; symbols: WatchlistSymbol[] }> {
  return request(
    `/api/watchlists/${encodeURIComponent(watchlistId)}/symbols`,
    jsonRequest('PUT', { tickers }),
  );
}

export function shouldRevalidateWatchlistAuth(error: unknown): boolean {
  return error instanceof WatchlistRequestError && isAuthStatus(error.status);
}

export function getActiveWatchlistStorageKey(userId: string | null, host: string): string {
  return `mvai:active-watchlist:${host}:${userId ?? 'anonymous'}`;
}

export function readActiveWatchlistPreference(userId: string | null): string {
  if (typeof window === 'undefined') {
    return DEFAULT_WATCHLIST_ID;
  }
  return (
    window.localStorage.getItem(getActiveWatchlistStorageKey(userId, window.location.host)) ??
    DEFAULT_WATCHLIST_ID
  );
}

export function writeActiveWatchlistPreference(userId: string | null, watchlistId: string): void {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(
      getActiveWatchlistStorageKey(userId, window.location.host),
      watchlistId,
    );
  }
}
